import { useFormik } from "formik"
import React, { useEffect, useState } from "react"
import { Alert, Button, Col, FloatingLabel, Form, Row, Spinner } from "react-bootstrap"
import { toast } from "react-toastify"
import * as Yup from "yup"

import CustomSelect from "../../../components/CustomSelect"
import ModalComponent from "../../../components/Modal"
import TooltipComponent from "../../../components/Tooltip"
import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import {
  generateRandomPassword,
  handleOnBlur,
  handleOnchange,
} from "../../../lib/GlobalMethods"

const statusOptions = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
]

const AddEditFleetVendor = ({
  isUpdate,
  setIsUpdate,
  show,
  setShow,
  fetchData,
  handleClose,
}) => {
  const [loading, setLoading] = useState(false)
  const [fleetClients, setFleetClients] = useState([])
  const [submitError, setSubmitError] = useState("")
  const [showPassword, setShowPassword] = useState(false)

  const handleGeneratePassword = () => {
    formik.setFieldValue("password", generateRandomPassword(10))
  }

  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .min(2, "Enter at least 2 characters for Fleet Vendor Name")
      .max(
        isUpdate ? 100 : 50,
        isUpdate
          ? "Fleet Vendor Name must not exceed 100 characters"
          : "Fleet Vendor Name must not exceed 50 characters to create its account",
      )
      .required("Fleet Vendor Name is required"),
    contact_no: Yup.string()
      .trim()
      .matches(
        /^[6-9]\d{9}$/,
        "Contact number must be a valid 10-digit mobile number",
        { excludeEmptyString: true },
      )
      .notRequired(),
    primary_fleet_client_id: Yup.string().required(
      "Primary Fleet Client is required",
    ),
    status: Yup.string().required("Status is required"),
    cost_per_kwh: Yup.number()
      .typeError("Cost per kWh must be a valid number")
      .test(
        "valid-cost-per-kwh",
        isUpdate
          ? "Cost per kWh cannot be negative"
          : "Cost per kWh must be greater than zero",
        (value) =>
          value !== undefined && (isUpdate ? value >= 0 : value > 0),
      )
      .required("Cost per kWh (₹) is required"),
    email: isUpdate
      ? Yup.string().notRequired()
      : Yup.string()
          .trim()
          .email("Enter a valid email address")
          .required("Email is required"),
    password: isUpdate
      ? Yup.string().notRequired()
      : Yup.string()
          .required("Password is required")
          .min(6, "Password should be 6 chars minimum.")
          .max(16, "Password should be 16 chars maximum.")
          .matches(
            /^\S.*\S$|^\S\S?$/,
            "Password cannot begin or end with spaces.",
          )
          .matches(/[a-zA-Z]/, "Password should contain at least one letter")
          .matches(/[^\w]/, "Password requires a symbol"),
  })

  const fetchFleetClients = async () => {
    const response = await axiosProvider({
      method: "GET",
      endpoint: `${admin.fleetClientList}?page=1&limit=200&active=true`,
    })
    const responseData = response?.data || response?.response?.data

    if (responseData?.code !== 200) {
      toast.error(responseData?.message || "Unable to fetch Fleet Clients")
      return
    }

    setFleetClients(
      (responseData.data || [])
        .filter((client) => client.status === "ACTIVE" && !client.deletedAt)
        .map((client) => ({ value: client.id, label: client.name })),
    )
  }

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setLoading(true)
      setSubmitError("")
      const response = await axiosProvider({
        method: isUpdate ? "PATCH" : "POST",
        endpoint: isUpdate
          ? admin.updateFleetVendor + isUpdate.id
          : admin.createFleetVendor,
        data: {
          name: values.name.trim(),
          contact_no: values.contact_no.trim() || null,
          primary_fleet_client_id: values.primary_fleet_client_id,
          status: values.status,
          cost_per_kwh: Number(values.cost_per_kwh),
          ...(!isUpdate && {
            email: values.email.trim().toLowerCase(),
            password: values.password,
          }),
        },
      })
      const responseData = response?.data || response?.response?.data

      if (responseData?.code !== 200 && responseData?.code !== 201) {
        throw new Error(
          responseData?.message ||
            responseData?.error ||
            response?.message ||
            "Unable to save Fleet Vendor. Please check the details and try again.",
        )
      }

      toast.success(responseData.message)
      setShow(false)
      setIsUpdate(false)
      setShowPassword(false)
      resetForm()
      fetchData()
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Unable to save Fleet Vendor. Please try again."
      setSubmitError(message)
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  const formik = useFormik({
    initialValues: isUpdate
      ? {
          name: isUpdate.name || "",
          contact_no: isUpdate.contact_no || "",
          primary_fleet_client_id:
            isUpdate.primary_fleet_client_id || "",
          status: isUpdate.status || "ACTIVE",
          cost_per_kwh:
            isUpdate.cost_per_kwh !== undefined &&
            isUpdate.cost_per_kwh !== null
              ? isUpdate.cost_per_kwh
              : "",
          email: isUpdate.admin_user?.email || isUpdate.email || "",
          password: "",
        }
      : {
          name: "",
          contact_no: "",
          primary_fleet_client_id: "",
          status: "ACTIVE",
          cost_per_kwh: "",
          email: "",
          password: "",
        },
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  })

  const { values, errors, touched, setFieldValue, resetForm } = formik

  useEffect(() => {
    if (show) fetchFleetClients()
  }, [show])

  return (
    <ModalComponent
      show={show}
      modalsize="lg"
      backdrop="static"
      animationFade={true}
      onHide={() => {
        handleClose()
        resetForm()
        setShowPassword(false)
      }}
      loading={loading}
      modeltitle={isUpdate ? "Update Fleet Vendor" : "Add Fleet Vendor"}
      submitname={
        loading ? (
          <Spinner size="sm" animation="border" variant="white" />
        ) : isUpdate ? (
          "Update"
        ) : (
          "Add"
        )
      }
      btnclosetext="Cancel"
      handleSubmit={formik.handleSubmit}
      children={
        <Form noValidate>
          {submitError && (
            <Alert
              variant="danger"
              role="alert"
              dismissible
              onClose={() => setSubmitError("")}
            >
              {submitError}
            </Alert>
          )}
          <Row>
            <Col xs={12} md={6}>
              <FloatingLabel
                controlId="fleetVendorName"
                label={<>Vendor Name <span className="required_star">*</span></>}
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  name="name"
                  placeholder=""
                  maxLength={isUpdate ? 100 : 50}
                  value={values.name}
                  onChange={(event) => handleOnchange(event, false, formik)}
                  onBlur={(event) => handleOnBlur(event, formik)}
                  isInvalid={touched.name && errors.name}
                  isValid={touched.name && !errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            <Col xs={12} md={6}>
              <FloatingLabel
                controlId="fleetVendorContactNo"
                label="Contact No. (Optional)"
                className="mb-3"
              >
                <Form.Control
                  type="tel"
                  name="contact_no"
                  placeholder=""
                  inputMode="numeric"
                  maxLength={10}
                  autoComplete="tel"
                  value={values.contact_no}
                  onChange={(event) =>
                    setFieldValue(
                      "contact_no",
                      event.target.value.replace(/\D/g, ""),
                    )
                  }
                  onBlur={formik.handleBlur}
                  isInvalid={touched.contact_no && errors.contact_no}
                  isValid={touched.contact_no && !errors.contact_no}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.contact_no}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            <Col xs={12} md={6}>
              <FloatingLabel
                controlId="fleetVendorCostPerKwh"
                label={<>Cost per kWh (₹) <span className="required_star">*</span></>}
                className="mb-3"
              >
                <Form.Control
                  type="number"
                  step="0.01"
                  min={isUpdate ? "0" : "0.01"}
                  name="cost_per_kwh"
                  placeholder=""
                  value={values.cost_per_kwh}
                  onChange={(event) => handleOnchange(event, false, formik)}
                  onBlur={(event) => handleOnBlur(event, formik)}
                  isInvalid={touched.cost_per_kwh && errors.cost_per_kwh}
                  isValid={touched.cost_per_kwh && !errors.cost_per_kwh}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.cost_per_kwh}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            {!isUpdate && (
              <>
                <Col xs={12} md={6}>
                  <FloatingLabel
                    controlId="fleetVendorAccountEmail"
                    label={<>Login Email <span className="required_star">*</span></>}
                    className="mb-3"
                  >
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder=""
                      autoComplete="email"
                      value={values.email}
                      onChange={(event) => handleOnchange(event, false, formik)}
                      onBlur={(event) => handleOnBlur(event, formik)}
                      isInvalid={touched.email && errors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Col>
                <Col xs={12} md={6}>
                  <div className="d-flex gap-3 align-items-start">
                    <FloatingLabel
                      controlId="fleetVendorAccountPassword"
                      label={<>Login Password <span className="required_star">*</span></>}
                      className="position-relative passwordicon_wrapper w-100"
                    >
                      <Form.Control
                        disabled
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder=""
                        autoComplete="new-password"
                        value={values.password}
                        onChange={(event) => handleOnchange(event, false, formik)}
                        onBlur={formik.handleBlur}
                        isInvalid={touched.password && errors.password}
                      />
                      <i
                        className={`icon_main ${showPassword ? "eye_close" : "eye_open"}`}
                        onClick={() => setShowPassword((current) => !current)}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.password}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                    <TooltipComponent toolplace="top" tooltipText="Generate password">
                      <Button
                        type="button"
                        variant="outline-primary"
                        className="btn btn-outline-primary d-inline-flex flex-shrink-0 px-2 py-2"
                        onClick={handleGeneratePassword}
                      >
                        <i className="icon_main clock_wise"></i>
                      </Button>
                    </TooltipComponent>
                  </div>
                </Col>
                <Col xs={12}>
                  <p className="text-muted">
                    This account will use the existing Vendor or Fleet Vendor role.
                  </p>
                </Col>
              </>
            )}

            {isUpdate && (
              <Col xs={12} md={6}>
                <FloatingLabel
                  controlId="fleetVendorAccountEmailReadOnly"
                  label="Login Email"
                  className="mb-3"
                >
                  <Form.Control
                    type="email"
                    value={values.email}
                    placeholder=""
                    readOnly
                    disabled
                  />
                </FloatingLabel>
              </Col>
            )}

            <Col xs={12} md={6} className="mb-3 custom_selectinvalid_bdr">
              <label>
                Primary Fleet Client <span className="required_star">*</span>
              </label>
              <CustomSelect
                value={fleetClients.find(
                  (client) => client.value === values.primary_fleet_client_id,
                ) || null}
                handleSelectChange={(selectedOption) =>
                  setFieldValue(
                    "primary_fleet_client_id",
                    selectedOption?.value || "",
                  )
                }
                options={fleetClients}
                placeholder="Select Primary Fleet Client"
              />
              {touched.primary_fleet_client_id &&
                errors.primary_fleet_client_id && (
                  <p className="invalid_error">
                    {errors.primary_fleet_client_id}
                  </p>
                )}
            </Col>

            {!isUpdate && (
              <Col xs={12} md={6} className="mb-3 custom_selectinvalid_bdr">
                <label>Status <span className="required_star">*</span></label>
                <CustomSelect
                  value={statusOptions.find(
                    (status) => status.value === values.status,
                  ) || null}
                  handleSelectChange={(selectedOption) =>
                    setFieldValue("status", selectedOption?.value || "ACTIVE")
                  }
                  options={statusOptions}
                  placeholder="Select Status"
                />
                {touched.status && errors.status && (
                  <p className="invalid_error">{errors.status}</p>
                )}
              </Col>
            )}
          </Row>
        </Form>
      }
    />
  )
}

export default AddEditFleetVendor
